"use client";

import "@/app/admin/admin.css";

import { AttachBatchBar } from "@/components/admin/attach-picker/attach-batch-bar";
import {
  AttachFileGrid,
  AttachFileGridViewport,
} from "@/components/admin/attach-picker/attach-file-grid";
import { AttachReplaceDialog } from "./attach-replace-dialog";
import { AttachRemoteQueue } from "./attach-remote-queue";
import type { AttachFileRecord } from "./types";
import { AttachFileList } from "@/components/admin/attach-picker/attach-file-list";
import { AttachGroupSidebar } from "@/components/admin/attach-picker/attach-group-sidebar";
import {
  AttachPanelBody,
  AttachSplitPanel,
  AttachUploadToolbarRow,
} from "@/components/admin/attach-picker/attach-panel-layout";
import { AttachViewModeToggle } from "@/components/admin/attach-picker/attach-view-mode-toggle";
import type {
  AttachPageSize,
  AttachPickerModalProps,
} from "@/components/admin/attach-picker/types";
import { useAttachPickerController } from "@/components/admin/attach-picker/use-attach-picker-controller";
import { AdminDeleteConfirmDialog } from "@/components/admin/admin-delete-confirm-dialog";
import {
  DialogCancelButton,
  AdminDialogHeader,
  SettingsSaveButton,
} from "@/components/admin/settings-form-controls";
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-field";
import { attachGroupPath, orderAttachGroups } from "@/lib/attach/group-tree";
import { SimpleSelect } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { CloudUploadIcon, FolderTreeIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { resolveAuthScopeFromPathname } from "@/lib/auth/auth-scope";
import { useRef, useState } from "react";
import {
  ADMIN_DIALOG_WIDTH_CLASS,
  adminDialogContentClass,
} from "@/hooks/use-admin-dialog";
import { AdminDialog } from "@/components/admin/admin-dialog-layout";
import {
  SettingsRow,
  SettingsRowGrid,
} from "@/components/admin/settings-field";
import {
  UPLOAD_CATEGORY_KINDS,
  type UploadCategoryKind,
} from "@/lib/upload/kinds";
import type { AttachVisibility } from "@/services/attach-service";
import { cn } from "@/lib/utils";

const attachPickerDialogClass = cn(
  adminDialogContentClass,
  "flex max-h-[92vh] flex-col",
  ADMIN_DIALOG_WIDTH_CLASS.lg,
);

export function AttachPickerModal(props: AttachPickerModalProps) {
  const { open, onOpenChange } = props;
  const pathname = usePathname();
  const canReplace = resolveAuthScopeFromPathname(pathname) === "admin";
  const t = useTranslations("site.attach");
  const {
    fileInputRef,
    pickerTexts,
    attachTabItems,
    tab,
    setTab,
    typeFilter,
    handleTypeFilterChange,
    groups,
    activeGroupId,
    items,
    selectedIds,
    pickedId,
    page,
    pageSize,
    setPage,
    setPageSize,
    total,
    totalPages,
    quotaText,
    quotaNotice,
    remoteTasks,
    handleRemoteRowsChange,
    handleRemotePaste,
    remoteCanUpload,
    remoteQueueProgress,
    rules,
    uploadContext,
    uploadVisibility,
    setUploadVisibility,
    initialLoading,
    listFetching,
    uploadTasks,
    remoteLoading,
    handleCancelRemote,
    selectionVisible,
    viewMode,
    setViewMode,
    moveOpen,
    setMoveOpen,
    moveFile,
    moveSaving,
    openMove,
    handleReplacedFile,
    activeGroupWritable,
    readOnlyGroupHint,
    moveTargetGroupId,
    setMoveTargetGroupId,
    deleteGroupTarget,
    setDeleteGroupTarget,
    fileDeleteTarget,
    setFileDeleteTarget,
    fileDeleteSaving,
    effectiveExtensions,
    uploading,
    allSelected,
    isMulti,
    canConfirm,
    handleToggleSelect,
    handlePick,
    handleConfirm,
    handleUploadFiles,
    handleRemoteExtract,
    handleDeleteFile,
    confirmDeleteFile,
    handleRenameFile,
    handleCancelUpload,
    handleBatchDelete,
    handleBatchMove,
    handleCreateGroup,
    handleUpdateGroup,
    handleDeleteGroup,
    confirmDeleteGroup,
    handleToggleSelectionMode,
    handleSelectGroup,
  } = useAttachPickerController(props);

  const [replaceFile, setReplaceFile] = useState<AttachFileRecord | null>(null);
  const [groupPanelOpen, setGroupPanelOpen] = useState(false);

  const [groupFormOpen, setGroupFormOpen] = useState(false);
  const [groupFormId, setGroupFormId] = useState<string | null>(null);
  const [groupFormName, setGroupFormName] = useState("");
  const [groupFormSaving, setGroupFormSaving] = useState(false);
  const [groupFormParentId, setGroupFormParentId] = useState<string | null>(null);
  const [groupFormSort, setGroupFormSort] = useState<number | null>(100);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const groupFormSubmitting = useRef(false);
  const editedGroup = groups.find(group => group.id === groupFormId);
  const groupHierarchyEditable = !groupFormId || Boolean(editedGroup?.sortable);
  const groupFormDirty = Boolean(groupFormName.trim()) && (!groupHierarchyEditable || groupFormSort !== null) && (
    !groupFormId || groupFormName.trim() !== editedGroup?.name || groupHierarchyEditable && (
      groupFormParentId !== editedGroup?.parentId || groupFormSort !== editedGroup?.sortOrder
    )
  );

  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);

  const hasDraggedFiles = (event: React.DragEvent) =>
    Array.from(event.dataTransfer?.types ?? []).includes("Files");

  const handleDragEnter = (event: React.DragEvent) => {
    if (tab !== "local" || uploading || remoteLoading || !hasDraggedFiles(event)) {
      return;
    }
    dragDepth.current += 1;
    setDragging(true);
  };

  const handleDragOver = (event: React.DragEvent) => {
    if (tab !== "local" || uploading || remoteLoading || !hasDraggedFiles(event)) {
      return;
    }
    event.preventDefault();
  };

  const handleDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) {
      setDragging(false);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    if (tab !== "local") {
      return;
    }
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (uploading || remoteLoading) {
      return;
    }
    const files = event.dataTransfer?.files
      ? Array.from(event.dataTransfer.files)
      : [];
    if (files.length > 0) {
      void handleUploadFiles(files);
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    if (tab !== "local" || uploading || remoteLoading) {
      return;
    }
    const files = event.clipboardData?.files
      ? Array.from(event.clipboardData.files)
      : [];
    if (files.length > 0) {
      event.preventDefault();
      void handleUploadFiles(files);
    }
  };

  const activeGroup = groups.find(group => group.id === activeGroupId);
  const activeGroupName = activeGroup ? attachGroupPath(activeGroup, groups) : t("allGroups");

  const handleSelectGroupMobile = (groupId: string) => {
    handleSelectGroup(groupId);
    setGroupPanelOpen(false);
  };

  const groupToggle = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-8 max-w-[45%] shrink-0 md:hidden"
      disabled={initialLoading || listFetching || uploading || remoteLoading}
      onClick={() => setGroupPanelOpen(true)}
    >
      <FolderTreeIcon />
      <span className="truncate">{activeGroupName}</span>
    </Button>
  );

  const typeFilterSelect = (
    <SimpleSelect
      size="sm"
      className="h-7 w-[84px] gap-0.5 border-0 bg-transparent px-1 py-0 text-muted-foreground shadow-none hover:text-foreground focus-visible:ring-0 dark:bg-transparent dark:hover:bg-transparent"

      align="start"
      alignItemWithTrigger={false}
      value={typeFilter}
      onValueChange={(value) =>
        handleTypeFilterChange(value as typeof typeFilter)
      }
      renderValue={(value) => (
        <span className="flex-1 truncate text-left">
          {value === "all"
            ? t("allTypes")
            : t(`kind.${value as UploadCategoryKind}`)}
        </span>
      )}
      options={[
        { value: "all", label: t("allTypes") },
        ...(props.allowedKinds ?? UPLOAD_CATEGORY_KINDS).map((categoryKind) => ({
          value: categoryKind,
          label: t(`kind.${categoryKind}`),
        })),
      ]}
    />
  );

  const groupSidebar = (
    <AttachGroupSidebar
      groups={groups}
      activeGroupId={activeGroupId}
      disabled={initialLoading || listFetching || uploading || remoteLoading}
      onSelectGroup={handleSelectGroupMobile}
      onRequestCreateGroup={() => {
        setGroupFormId(null);
        setGroupFormName("");
        setGroupFormParentId(null);
        setGroupFormSort(100);
        setGroupFormOpen(true);
      }}
      onRequestRenameGroup={(group) => {
        setGroupFormId(group.id);
        setGroupFormName(group.name);
        setGroupFormParentId(group.parentId);
        setGroupFormSort(group.sortOrder);
        setGroupFormOpen(true);
      }}
      onDeleteGroup={handleDeleteGroup}
      expandedGroups={expandedGroups}
      onToggleExpand={(id, expanded) => setExpandedGroups(current => ({ ...current, [id]: expanded }))}
    />
  );

  async function submitGroupForm() {
    const name = groupFormName.trim();

    if (!groupFormDirty || groupFormSubmitting.current) {
      return;
    }

    setGroupFormSaving(true);
    groupFormSubmitting.current = true;
    try {
      const input = { name, ...(groupHierarchyEditable ? { parentId: groupFormParentId, sortOrder: groupFormSort! } : {}) };
      const ok = groupFormId ? await handleUpdateGroup(groupFormId, input) : await handleCreateGroup(input);
      if (ok) {
        if (groupFormParentId) setExpandedGroups(current => ({ ...current, [groupFormParentId]: true }));
        setGroupFormOpen(false);
        setGroupFormId(null);
        setGroupFormName("");
      }
    } finally { groupFormSubmitting.current = false; setGroupFormSaving(false); }
  }

  const uploadHint = !activeGroupWritable
    ? readOnlyGroupHint
    : rules
      ? t("storedLocalMax", { max: rules.maxSizeMb })
      : t("storedLocal");

  const visibilitySelect =
    uploadContext?.visibilitySelectable &&
    uploadContext.visibilityOptions.length > 0 ? (
      <SimpleSelect
        value={uploadVisibility}
        disabled={remoteLoading || uploading || initialLoading}
        onValueChange={(value) => setUploadVisibility(value as AttachVisibility)}
        placeholder={t("visibility")}
        className="h-8 w-[140px]"
        options={uploadContext.visibilityOptions.map((option) => ({
          value: option.value,
          label: option.label,
        }))}
      />
    ) : null;

  const batchBarProps = {
    total,
    page,
    totalPages,
    pageSize,
    selectedCount: selectedIds.length,
    allSelected,
    selectionVisible,
    disabled: initialLoading || listFetching || uploading,
    onToggleSelectionMode: handleToggleSelectionMode,
    onMove: () => openMove(),
    onDelete: () => void handleBatchDelete(),
    onPageChange: setPage,
    onPageSizeChange: (nextPageSize: AttachPageSize) => {
      setPageSize(nextPageSize);
      setPage(1);
    },
  };

  const panelToolbar = (
    <AttachUploadToolbarRow
      leading={groupToggle}
      uploadLabel={pickerTexts.uploadButton}
      hint={uploadHint}
      fileInputRef={fileInputRef}
      accept={rules?.accept}
      uploading={uploading}
      uploadDisabled={!activeGroupWritable || remoteLoading}
      trailing={visibilitySelect}
      startActions={
        <div className="flex items-center gap-2">
          <AttachViewModeToggle
            mode={viewMode}
            disabled={initialLoading || uploading}
            onChange={setViewMode}
          />
          {typeFilterSelect}
        </div>
      }
      onFileChange={(event) => {
        const selectedFiles = event.target.files
          ? Array.from(event.target.files)
          : [];
        event.target.value = "";

        if (selectedFiles.length > 0) {
          void handleUploadFiles(selectedFiles);
        }
      }}
      onUploadClick={() => fileInputRef.current?.click()}
    />
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-admin-dialog
          className={attachPickerDialogClass}
          onPaste={handlePaste}
        >
          <AdminDialogHeader
            title={pickerTexts.title}
            subtitle={
              <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="inline-flex items-center gap-1">
                  {t("quotaLabel")}
                  {quotaText ? (
                    quotaText
                  ) : (
                    <Skeleton className="inline-block h-3 w-24 rounded align-middle" />
                  )}
                </span>
                {quotaNotice ? (
                  <span
                    className={cn(
                      quotaNotice.tone === "danger"
                        ? "text-destructive"
                        : "text-amber-600 dark:text-amber-500",
                    )}
                  >
                    {quotaNotice.text}
                  </span>
                ) : null}
              </span>
            }
          />

          <Tabs
            value={tab}
            onValueChange={(value) => setTab(value as "local" | "remote")}
            className="flex min-h-0 flex-1 flex-col gap-0"
          >
            <div className="w-full shrink-0 border-b border-border">
              <div
                role="tablist"
                aria-label={pickerTexts.sourceAriaLabel}
                className="flex w-full items-end justify-center gap-[50px] px-4"
              >
                {attachTabItems.map((item) => {
                  const active = tab === item.value;

                  return (
                    <button
                      key={item.value}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setTab(item.value)}
                      className={cn(
                        "-mb-px box-border w-[120px] shrink-0 cursor-pointer border-b-2 py-[15px] text-center text-base font-medium transition-colors",
                        active
                          ? "border-foreground text-foreground"
                          : "border-transparent text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <TabsContent
              value="local"
              className="relative flex min-h-0 flex-1 flex-col"
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {dragging ? (
                <div className="pointer-events-none absolute inset-2 z-30 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-primary bg-background/85 text-primary">
                  <CloudUploadIcon className="size-8" />
                  <p className="text-sm font-medium">{t("dropToUpload")}</p>
                </div>
              ) : null}
              <AttachSplitPanel
                sidebar={groupSidebar}
                open={groupPanelOpen}
                onClose={() => setGroupPanelOpen(false)}
              >
                <AttachPanelBody
                  toolbar={panelToolbar}
                  viewport={
                    <AttachFileGridViewport
                      loading={initialLoading}
                      emptyOverlay={
                        items.length === 0 && uploadTasks.length === 0
                          ? pickerTexts.emptyHint
                          : undefined
                      }
                    >
                      {viewMode === "grid" ? (
                        <AttachFileGrid
                          items={items}
                          uploadTasks={uploadTasks}
                          selectedIds={selectedIds}
                          pickedId={pickedId}
                          selectionVisible={selectionVisible}
                          disabled={uploading}
                          onToggleSelect={handleToggleSelect}
                          onPick={handlePick}
                          onDelete={(id) => void handleDeleteFile(id)}
                          onReplace={canReplace ? setReplaceFile : undefined}
                          onMove={openMove}
                          onRename={(id, originalName) =>
                            void handleRenameFile(id, originalName)
                          }
                          onCancelUpload={handleCancelUpload}
                        />
                      ) : (
                        <AttachFileList
                          items={items}
                          uploadTasks={uploadTasks}
                          selectedIds={selectedIds}
                          pickedId={pickedId}
                          disabled={uploading}
                          onToggleSelect={handleToggleSelect}
                          onPick={handlePick}
                          onDelete={(id) => void handleDeleteFile(id)}
                          onReplace={canReplace ? setReplaceFile : undefined}
                          onMove={openMove}
                          onRename={(id, originalName) =>
                            void handleRenameFile(id, originalName)
                          }
                          onCancelUpload={handleCancelUpload}
                        />
                      )}
                    </AttachFileGridViewport>
                  }
                  footer={<AttachBatchBar {...batchBarProps} />}
                />
              </AttachSplitPanel>
            </TabsContent>

            <TabsContent value="remote" className="flex min-h-0 flex-1 flex-col">
              <AttachSplitPanel
                sidebar={groupSidebar}
                open={groupPanelOpen}
                onClose={() => setGroupPanelOpen(false)}
              >
                <AttachPanelBody
                  toolbar={panelToolbar}
                  viewport={<AttachFileGridViewport>{null}</AttachFileGridViewport>}
                  footer={<AttachBatchBar {...batchBarProps} />}
                  overlay={
                    <div className="flex min-h-0 flex-1 flex-col px-4 py-3">
                      <div className="flex shrink-0 items-center gap-2 md:hidden">{groupToggle}</div>
                      {visibilitySelect ? (
                        <div className="flex shrink-0 items-center gap-2 pb-2">
                          <span className="text-sm text-muted-foreground">{t("visibility")}</span>
                          {visibilitySelect}
                        </div>
                      ) : null}
                      <AttachRemoteQueue
                        tasks={remoteTasks}
                        busy={remoteLoading}
                        disabled={initialLoading || !activeGroupWritable || uploading}
                        canUpload={remoteCanUpload}
                        progress={remoteQueueProgress}
                        placeholder={pickerTexts.remotePlaceholder}
                        hint={rules
                          ? t("remoteHintRules", { max: rules.maxSizeMb, extensions: effectiveExtensions.join(t("listSeparator")) })
                          : t("remoteHint")}
                        onChange={handleRemoteRowsChange}
                        onPaste={handleRemotePaste}
                        onUpload={() => void handleRemoteExtract()}
                        onCancel={handleCancelRemote}
                      />
                    </div>
                  }
                />
              </AttachSplitPanel>
            </TabsContent>
          </Tabs>

          <DialogFooter className="grid shrink-0 grid-cols-2 gap-[15px] sm:flex sm:gap-2">
            <DialogCancelButton onClick={() => onOpenChange(false)} />
            <SettingsSaveButton
              dirty={canConfirm}
              type="button"
              onClick={handleConfirm}
            >
              {isMulti && selectedIds.length > 0
                ? t("confirmCount", { count: selectedIds.length })
                : t("confirm")}
            </SettingsSaveButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AdminDialog
        open={groupFormOpen}
        onOpenChange={(open) => {
          setGroupFormOpen(open);

          if (!open) {
            setGroupFormId(null);
            setGroupFormName("");
          }
        }}
        title={groupFormId ? t("editGroup") : t("addGroup")}
        size="sm"
        saving={groupFormSaving}
        cancel={{ onClick: () => setGroupFormOpen(false) }}
        confirm={{
          dirty: groupFormDirty,
          label: t("save"),
          onClick: () => void submitGroupForm(),
        }}
      >
        <SettingsRowGrid labelChars={4}>
          {groupHierarchyEditable ? <SettingsRow label={t("parentGroup")} htmlFor="attach-group-parent">
            <SimpleSelect
              id="attach-group-parent"
              className="w-full"
              value={groupFormParentId ?? "__top__"}
              disabled={groupFormSaving || Boolean(editedGroup?.childCount)}
              onValueChange={value => setGroupFormParentId(value === "__top__" ? null : value)}
              options={[
                { value: "__top__", label: t("topGroup") },
                ...orderAttachGroups(groups).filter(group => group.scope === "personal" && !group.isDefault && !group.parentId && group.id !== groupFormId && group.writable).map(group => ({ value: group.id, label: group.name })),
              ]}
            />
          </SettingsRow> : null}
          <SettingsRow label={t("groupName")} htmlFor="attach-group-name" required>
            <Input
              id="attach-group-name"
              value={groupFormName}
              maxLength={50}
              autoFocus
              placeholder={t("groupNamePlaceholder")}
              disabled={groupFormSaving}
              onChange={(event) => setGroupFormName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void submitGroupForm();
                }
              }}
            />
          </SettingsRow>
          {groupHierarchyEditable ? <SettingsRow label={t("groupSort")} htmlFor="attach-group-sort" required>
            <NumberInput id="attach-group-sort" value={groupFormSort} min={0} max={2_147_483_647} step={1} disabled={groupFormSaving} onValueChange={setGroupFormSort} />
          </SettingsRow> : null}
        </SettingsRowGrid>
      </AdminDialog>

      <AdminDialog
        open={moveOpen}
        onOpenChange={(open) => { if (!moveSaving) setMoveOpen(open); }}
        saving={moveSaving}
        title={t("moveTitle")}
        size="sm"
        cancel={{ onClick: () => setMoveOpen(false) }}
        confirm={{
          dirty: Boolean(moveTargetGroupId),
          label: t("move"),
          onClick: () => void handleBatchMove(),
        }}
      >
        <SimpleSelect
          disabled={moveSaving}
          value={moveTargetGroupId}
          onValueChange={(value) => setMoveTargetGroupId(value)}
          placeholder={t("moveTargetPlaceholder")}
          className="w-full"
          options={orderAttachGroups(groups)
            .filter((group) => group.id !== (moveFile?.groupId ?? activeGroupId) && group.writable)
            .map((group) => ({ value: group.id, label: attachGroupPath(group, groups) }))}
        />
      </AdminDialog>

      {canReplace && <AttachReplaceDialog file={open ? replaceFile : null} onClose={() => setReplaceFile(null)} onReplaced={handleReplacedFile} />}

      <AdminDeleteConfirmDialog
        open={!!deleteGroupTarget}
        onOpenChange={(open) => !open && setDeleteGroupTarget(null)}
        content={t("deleteGroupContent")}
        size="sm"
        onCancel={() => setDeleteGroupTarget(null)}
        onConfirm={() => void confirmDeleteGroup()}
      />

      {/* 删文件是行内操作：气泡贴着刚点的删除入口出现，不把视线拽到屏幕正中 */}
      <AdminDeleteConfirmDialog
        open={!!fileDeleteTarget}
        onOpenChange={(open) => !open && setFileDeleteTarget(null)}
        anchor="pointer"
        align="end"
        title={
          fileDeleteTarget?.mode === "batch"
            ? t("deleteSelectedTitle", { count: selectedIds.length })
            : t("deleteOneTitle")
        }
        content={t("deleteFileContent")}
        size="sm"
        saving={fileDeleteSaving}
        onCancel={() => setFileDeleteTarget(null)}
        onConfirm={() => void confirmDeleteFile()}
      />
    </>
  );
}
